import React from 'react'
import Styles from './styles'

export const ACTION_CARD_PROPS = {
  HOLIDAYS: {
    backgroundColor: '#F6F8FF',
    color: '#FF9F43',
    badgeContent: 'Datas especiais',
  },
  GENERAL_ALERTS: {
    backgroundColor: '#FFF',
    color: '#F9F9F9',
    badgeContent: 'Alertas',
  },
}

const ActionCardContext = React.createContext()

/**
 * ActionCard component provides a context-based structure for creating styled cards
 * with different variants and compound components.
 *
 * @param {Object} props - The props for the ActionCard component.
 * @param {React.ReactNode} props.children - The content to be rendered inside the ActionCard.
 * @param {keyof typeof ACTION_CARD_PROPS} [props.variant='GENERAL_ALERTS'] - The variant of the ActionCard. Can be one of the keys in `ACTION_CARD_TYPES`.
 * @returns {JSX.Element} The rendered ActionCard component.
 */
export default function ActionCard({ children, variant = 'GENERAL_ALERTS' }) {
  const values = ACTION_CARD_PROPS[variant] || ACTION_CARD_PROPS.GENERAL_ALERTS
  return (
    <ActionCardContext.Provider value={{ variant, ...values }}>
      <Container>{children}</Container>
    </ActionCardContext.Provider>
  )
}

ActionCard.Title = Styles.Title
ActionCard.Description = Styles.Description
ActionCard.ButtonsWrapper = Styles.ButtonsWrapper
ActionCard.ContentWrapper = Styles.ContentWrapper
ActionCard.TextWrapper = Styles.TextWrapper

const useActionCardContext = () => {
  const context = React.useContext(ActionCardContext)
  if (!context) {
    throw new Error(
      'ActionCard compound components cannot be rendered outside the ActionCard component'
    )
  }
  return context
}

const Container = ({ children }) => {
  const { backgroundColor, color } = useActionCardContext()
  return (
    <Styles.Container backgroundColor={backgroundColor} color={color}>
      {children}
    </Styles.Container>
  )
}

function Badge({ children }) {
  const { variant } = useActionCardContext()
  if (variant === 'HOLIDAYS') {
    return <Styles.SazonalDatesBadge>{children}</Styles.SazonalDatesBadge>
  }
  if (variant === 'GENERAL_ALERTS') {
    return <Styles.GeneralAlertsBadge>{children}</Styles.GeneralAlertsBadge>
  }
  return null
}

Badge.SazonalDates = Styles.SazonalDatesBadge
Badge.GeneralAlerts = Styles.GeneralAlertsBadge
ActionCard.Badge = Badge

function Button({ children }) {
  const { variant } = useActionCardContext()
  if (variant === 'HOLIDAYS') {
    return <Styles.SazonalDatesButton>{children}</Styles.SazonalDatesButton>
  }
  if (variant === 'GENERAL_ALERTS') {
    return <Styles.GeneralAlertsButton>{children}</Styles.GeneralAlertsButton>
  }
  return null
}

Button.GeneralAlerts = Styles.GeneralAlertsButton
Button.SazonalDates = Styles.SazonalDatesButton
Button.Ignore = Styles.IgnoreButton
ActionCard.Button = Button
